/**
 * CreateAccountScreen — register a brand-new on-chain account, paid for by
 * the currently active (logged-in) account: newaccount + buyrambytes +
 * optional delegatebw in one transaction. Mirrors blocksfinity.com's
 * /wallet/createaccount, reachable from Tools and from the account switcher.
 */

import { useEffect, useState } from 'react'
import type { Session } from '@wharfkit/session'
import { PublicKey } from '@wharfkit/antelope'
import { accountManager } from '@properwallet/wallet-core'
import {
  createAccount,
  generateKeyPair,
  isValidAccountName,
  isAccountNameAvailable,
  MIN_NEW_ACCOUNT_RAM_BYTES,
  fetchRamPrice,
  ramBytesForEos,
  ramEosForBytes,
  type RamPrice,
} from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import PinInput, { PIN_LENGTH } from '../components/PinInput'

type NameStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
type KeyMode = 'generate' | 'own'

const CPU_PRESETS = ['0.0000', '0.1000', '0.5000', '1.0000']
const NET_PRESETS = ['0.0000', '0.1000', '0.5000', '1.0000']

export default function CreateAccountScreen() {
  const { t } = useTranslation()
  const { activeAccount, activeSession, goBack, navigate, init, encryptPinForNewAccount, unlockWithPin } =
    useWalletStore()

  const [newName, setNewName] = useState('')
  const [nameStatus, setNameStatus] = useState<NameStatus>('idle')

  const [keyMode, setKeyMode] = useState<KeyMode>('generate')
  const [generated] = useState(() => generateKeyPair())
  const [ownerKeyInput, setOwnerKeyInput] = useState('')
  const [activeKeyInput, setActiveKeyInput] = useState('')
  const [sameKey, setSameKey] = useState(true)

  const [ramKb, setRamKb] = useState((MIN_NEW_ACCOUNT_RAM_BYTES / 1024).toFixed(2))
  const [ramPrice, setRamPrice] = useState<RamPrice | null>(null)
  const [cpuStake, setCpuStake] = useState('0.1000')
  const [netStake, setNetStake] = useState('0.1000')
  const [transferStake, setTransferStake] = useState(false)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [txid, setTxid] = useState('')
  const [needsPin, setNeedsPin] = useState(false)
  const [postPin, setPostPin] = useState('')
  const [postPinError, setPostPinError] = useState('')

  useEffect(() => {
    void fetchRamPrice().then(p => { if (p) setRamPrice(p) })
  }, [])

  // Debounced name availability check.
  useEffect(() => {
    const n = newName.trim().toLowerCase()
    if (!n) { setNameStatus('idle'); return }
    if (!isValidAccountName(n)) { setNameStatus('invalid'); return }
    setNameStatus('checking')
    const timer = setTimeout(() => {
      void isAccountNameAvailable(n).then(ok => setNameStatus(ok ? 'available' : 'taken'))
    }, 400)
    return () => clearTimeout(timer)
  }, [newName])

  const ramBytes = Math.max(MIN_NEW_ACCOUNT_RAM_BYTES, Math.round((parseFloat(ramKb) || 0) * 1024))
  const ramCost = ramPrice ? ramEosForBytes(ramBytes, ramPrice) : 0

  const ownerKey = keyMode === 'generate' ? generated.publicKey : ownerKeyInput.trim()
  const activeKey = keyMode === 'generate'
    ? generated.publicKey
    : (sameKey ? ownerKeyInput.trim() : activeKeyInput.trim())

  const keysValid = (() => {
    if (keyMode === 'generate') return true
    try {
      PublicKey.from(ownerKey)
      PublicKey.from(activeKey)
      return true
    } catch { return false }
  })()

  const canSubmit =
    !!activeAccount && !!activeSession && nameStatus === 'available' && keysValid && !busy

  const handleCreate = async () => {
    if (!activeAccount || !activeSession) { setError(t('createAccount.errorPayer')); return }
    if (nameStatus !== 'available') { setError(t('createAccount.errorName')); return }
    if (!keysValid) { setError(t('createAccount.errorKey')); return }

    setError('')
    setBusy(true)
    try {
      const session = activeSession as Session
      const payerPermission = session.permission.toString()
      const id = await createAccount(session, {
        payer: activeAccount.name,
        newAccount: newName.trim().toLowerCase(),
        ownerKey,
        activeKey,
        ramBytes,
        cpuStakeEos: `${cpuStake || '0'} EOS`,
        netStakeEos: `${netStake || '0'} EOS`,
        transferStake,
        payerPermission,
      })
      setTxid(id)

      if (keyMode === 'generate') {
        await accountManager.addAccount(generated.privateKey, { accountName: newName.trim().toLowerCase() })
        await init()
        const hasPin = await accountManager.hasPin()
        if (hasPin) setNeedsPin(true)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const handlePostPin = async (pin: string) => {
    if (pin.length !== PIN_LENGTH) return
    setPostPinError('')
    try {
      await encryptPinForNewAccount(newName.trim().toLowerCase(), pin)
      await unlockWithPin(pin)
      setNeedsPin(false)
    } catch (e) {
      setPostPinError(e instanceof Error ? e.message : t('common.error'))
      setPostPin('')
    }
  }

  // ── Success ──────────────────────────────────────────────────────────
  if (txid && !needsPin) {
    return (
      <div className="flex flex-col h-full bg-surface">
        <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
          <h2 className="text-white font-semibold flex-1 text-center">{t('createAccount.title')}</h2>
        </div>
        <div className="scroll-area flex-1 px-4 py-6 space-y-4">
          <div className="card-highlighted text-center">
            <p className="text-white font-semibold text-lg">✓ {t('createAccount.success')}</p>
            <p className="text-brand-light font-mono text-sm mt-1">{newName.trim().toLowerCase()}</p>
            <p className="text-gray-500 text-xs mt-2 break-all">{txid}</p>
          </div>

          {keyMode === 'generate' && (
            <div className="card space-y-2">
              <p className="text-danger text-xs">{t('createAccount.keyWarning')}</p>
              <p className="text-gray-400 text-xs">{t('createAccount.privateKeyLabel')}</p>
              <p className="input-field-mono break-all" style={{ height: 'auto', paddingTop: 10, paddingBottom: 10 }}>
                {generated.privateKey}
              </p>
              <button
                className="btn-secondary w-full text-sm"
                onClick={() => navigator.clipboard?.writeText(generated.privateKey)}
              >
                {t('createAccount.copyKey')}
              </button>
            </div>
          )}

          <button className="btn-primary w-full" onClick={() => navigate('tools')}>
            {t('common.done')}
          </button>
        </div>
      </div>
    )
  }

  // ── Post-create PIN step (wraps the new key under the wallet's existing PIN) ──
  if (needsPin) {
    return (
      <div className="flex flex-col h-full pt-safe px-4 pb-8 justify-center bg-surface">
        <h2 className="text-lg font-semibold text-white text-center mb-2">{t('pin.enter')}</h2>
        <p className="text-sm text-gray-400 text-center mb-6">{t('createAccount.savingKey')}</p>
        <PinInput
          value={postPin}
          onChange={v => { setPostPin(v); setPostPinError(''); if (v.length === PIN_LENGTH) void handlePostPin(v) }}
        />
        {postPinError ? <p className="text-danger text-sm text-center mt-4">{postPinError}</p> : null}
      </div>
    )
  }

  // ── Form ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-surface">
      <div className="bg-surface-1 pt-safe px-4 py-3 border-b border-surface-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={goBack} className="text-gray-400 text-sm">← {t('common.back')}</button>
        <h2 className="text-white font-semibold flex-1 text-center">{t('createAccount.title')}</h2>
        <div className="w-16" />
      </div>

      <div className="scroll-area flex-1 px-4 py-4 space-y-4">
        <div className="card space-y-1">
          <p className="text-xs text-gray-400">{t('createAccount.payer')}</p>
          <p className="text-white font-mono text-sm">{activeAccount?.name ?? '—'}</p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-gray-400">{t('createAccount.newAccountName')}</label>
          <input
            className="input-field font-mono"
            value={newName}
            onChange={e => setNewName(e.target.value.toLowerCase())}
            placeholder="myaccount12"
            maxLength={12}
          />
          <p className="text-xs" style={{
            color: nameStatus === 'available' ? '#4ade80'
              : nameStatus === 'taken' || nameStatus === 'invalid' ? '#ef4444'
              : '#6b7280',
          }}>
            {nameStatus === 'checking' && t('createAccount.checking')}
            {nameStatus === 'available' && `✓ ${t('createAccount.nameAvailable')}`}
            {nameStatus === 'taken' && t('createAccount.nameTaken')}
            {nameStatus === 'invalid' && t('createAccount.nameInvalid')}
            {nameStatus === 'idle' && t('createAccount.newAccountNameHint')}
          </p>
        </div>

        <div className="card space-y-3">
          <p className="text-sm font-semibold text-white">{t('createAccount.keyMode')}</p>
          <div className="flex gap-2">
            <button
              className={`flex-1 py-2 rounded-lg text-xs ${keyMode === 'generate' ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}
              onClick={() => setKeyMode('generate')}
            >
              {t('createAccount.keyModeGenerate')}
            </button>
            <button
              className={`flex-1 py-2 rounded-lg text-xs ${keyMode === 'own' ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}
              onClick={() => setKeyMode('own')}
            >
              {t('createAccount.keyModeOwn')}
            </button>
          </div>

          {keyMode === 'generate' ? (
            <p className="input-field-mono break-all" style={{ height: 'auto', paddingTop: 10, paddingBottom: 10 }}>
              {generated.publicKey}
            </p>
          ) : (
            <div className="space-y-2">
              <input
                className="input-field-mono"
                value={ownerKeyInput}
                onChange={e => setOwnerKeyInput(e.target.value.trim())}
                placeholder={t('createAccount.ownerKey')}
              />
              <label className="flex items-center gap-2 text-xs text-gray-400">
                <input type="checkbox" checked={sameKey} onChange={e => setSameKey(e.target.checked)} />
                {t('createAccount.sameKeyForBoth')}
              </label>
              {!sameKey && (
                <input
                  className="input-field-mono"
                  value={activeKeyInput}
                  onChange={e => setActiveKeyInput(e.target.value.trim())}
                  placeholder={t('createAccount.activeKey')}
                />
              )}
            </div>
          )}
        </div>

        <div className="card space-y-3">
          <p className="text-sm font-semibold text-white">{t('createAccount.ram')}</p>
          <input type="number" value={ramKb} onChange={e => setRamKb(e.target.value)} className="input-field text-sm py-2" />
          <p className="text-xs text-gray-500">
            {(ramBytes / 1024).toFixed(2)} KB {ramPrice ? `≈ ${ramCost.toFixed(4)} EOS` : ''}
          </p>
        </div>

        <div className="card space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('createAccount.cpuStake')}</label>
            <div className="flex gap-2 flex-wrap mb-1">
              {CPU_PRESETS.map(p => (
                <button key={p} onClick={() => setCpuStake(p)}
                  className={`px-2 py-1 rounded-lg text-xs ${cpuStake === p ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}>
                  {p}
                </button>
              ))}
            </div>
            <input type="number" value={cpuStake} onChange={e => setCpuStake(e.target.value)} className="input-field text-sm py-2" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-gray-400">{t('createAccount.netStake')}</label>
            <div className="flex gap-2 flex-wrap mb-1">
              {NET_PRESETS.map(p => (
                <button key={p} onClick={() => setNetStake(p)}
                  className={`px-2 py-1 rounded-lg text-xs ${netStake === p ? 'bg-brand text-white' : 'bg-surface-2 text-gray-400'}`}>
                  {p}
                </button>
              ))}
            </div>
            <input type="number" value={netStake} onChange={e => setNetStake(e.target.value)} className="input-field text-sm py-2" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400">
            <input type="checkbox" checked={transferStake} onChange={e => setTransferStake(e.target.checked)} />
            {t('createAccount.transferStake')}
          </label>
        </div>

        {error ? <p className="text-danger text-xs text-center">{error}</p> : null}

        <button className="btn-primary w-full" disabled={!canSubmit} onClick={handleCreate}>
          {busy ? t('createAccount.creating') : t('createAccount.create')}
        </button>
      </div>
    </div>
  )
}
